import t from "@/lang/lang";
import { TextInputField } from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { Address } from "@/services/backend/addresses/types/address";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";

/**
 * AddressFieldsDebounced collects an address for an auto-saving form.
 *
 * It differs from {@link AddressFields} in that its inputs debounce, so
 * the caller can render straight from the RTK Query cache and save through
 * useOptimisticDebouncedMutation without a local state buffer.
 *
 * AddressFields is the older local-state variant and is kept for the call
 * sites that have not been migrated yet.
 *
 * Its fields read their errors from the ErrValidationFailedContext at
 * `${fieldName}.<field>` and reset them on input. `error` is an escape hatch
 * for pages without a ErrValidationFailedContextProvider: the error of the
 * mutation, whose field messages are picked out by the same keys.
 */
export function AddressFieldsDebounced({
  address,
  onAddressChange,
  error = undefined,
  fieldName = "address",
  disabled = false,
}: {
  address: Address;
  onAddressChange: (address: Address) => void;
  error?: unknown;
  /** The field name of the address in the ErrValidationFailedContext. */
  fieldName?: string;
  disabled?: boolean;
}) {
  const errors = (field: string) => ({
    fieldName: joinFieldName(fieldName, field),
    errors: inputValidationErrorMessages(
      error,
      joinFieldName(fieldName, field),
    ),
  });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextInputField
        label={t("Firma")}
        text={address.company}
        onTextChange={(company) => onAddressChange({ ...address, company })}
        {...errors("company")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Abteilung")}
        text={address.department}
        onTextChange={(department) =>
          onAddressChange({ ...address, department })
        }
        {...errors("department")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Ansprechpartner")}
        text={address.contact}
        onTextChange={(contact) => onAddressChange({ ...address, contact })}
        {...errors("contact")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Straße")}
        text={address.street}
        onTextChange={(street) => onAddressChange({ ...address, street })}
        {...errors("street")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Postfach")}
        text={address.postbox}
        onTextChange={(postbox) => onAddressChange({ ...address, postbox })}
        {...errors("postbox")}
        disabled={disabled}
      />
      <div className="flex gap-4">
        <TextInputField
          label={t("PLZ")}
          fieldProps={{ className: "max-w-32" }}
          text={address.postalCode}
          onTextChange={(postalCode) =>
            onAddressChange({ ...address, postalCode })
          }
          {...errors("postalCode")}
          disabled={disabled}
        />
        <TextInputField
          label={t("Stadt")}
          text={address.city}
          onTextChange={(city) => onAddressChange({ ...address, city })}
          {...errors("city")}
          disabled={disabled}
        />
      </div>
      <TextInputField
        label={t("Land")}
        text={address.country}
        onTextChange={(country) => onAddressChange({ ...address, country })}
        {...errors("country")}
        disabled={disabled}
      />
      <TextareaField
        label={t("Kommentar")}
        description={t(
          "Hinweise zur Erreichbarkeit, Gebäudenummern und Ähnliches.",
        )}
        value={address.comment}
        onChange={(e) =>
          onAddressChange({ ...address, comment: e.target.value })
        }
        {...errors("comment")}
        disabled={disabled}
      />
    </div>
  );
}
